import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Accordion } from '@appcues/sonar';
import { FieldSet } from 'ext/components/ui';
import BlockContentShape from 'entities/block/shape';
import {
  Content,
  Controls,
  Style,
  SizeSpacing,
  ThemeVariantSelect,
} from 'components/SideBarSettings/Shared';
import ContentActions from 'components/SideBarSettings/Content/ContentActions';
import { IMAGE } from 'lib/block/block';
import ImageUploader from 'components/ImageUploader';
import {
  DEFAULT_IMAGE_URL,
  DEFAULT_ICON_URL,
} from 'components/Editor/Templates/Content';
import Accessibility from './Accessibility';

export function ImageSettings({
  content,
  contentWithVariations,
  blockType,
  onChange,
}) {
  const { id: blockId, imageUrl } = content ?? {};
  const defaultImage =
    blockType === IMAGE ? DEFAULT_IMAGE_URL : DEFAULT_ICON_URL;
  const showImageInUploadBox = imageUrl !== defaultImage;

  const handleImage = useCallback(
    params => {
      // If no params (the image has been deleted), reset to the default image
      const { url, intrinsicSize } = params ?? {
        url: defaultImage,
        intrinsicSize: { width: 1920, height: 1080 },
      };

      onChange({
        blockId,
        contentChunk: { imageUrl: url, blurHash: '', intrinsicSize },
      });
    },
    [blockId, onChange, defaultImage]
  );

  return (
    <Content>
      <ThemeVariantSelect
        type={blockType}
        content={content}
        contentWithVariations={contentWithVariations}
        onChange={onChange}
      />
      <Controls>
        <FieldSet>
          <ImageUploader
            description="Upload image or .svg"
            currentImage={showImageInUploadBox ? imageUrl : undefined}
            onUpload={handleImage}
          />
        </FieldSet>
      </Controls>
      <Accordion.Root
        collapsible="true"
        defaultValue={['style', 'size-spacing', 'accessibility']}
        type="multiple"
      >
        <Style content={content} onChange={onChange} />
        <SizeSpacing
          content={content}
          onChange={onChange}
          hasMargin
          horizontalAlignmentOnly
        />
        <Accessibility content={content} onChange={onChange} />
        <ContentActions blockId={blockId} context={IMAGE} />
      </Accordion.Root>
    </Content>
  );
}

ImageSettings.propTypes = {
  content: BlockContentShape,
  contentWithVariations: BlockContentShape,
  blockType: PropTypes.string,
  onChange: PropTypes.func,
};

export default ImageSettings;
